import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useRef, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { useAppDispatch } from "app/hooks";
import {
  setAdditionalAction,
  setCustomMessage,
  setLabel,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { ZOOM_HOST_START_LINK_FEATURE } from "constants/feature-flags";
import { useGetZoomStartLinkMutation } from "services/appointments";

/** Why the host link could not be issued, as coded by `zoom-start-link/` (ENG-2085). */
type HostStartFailureCode =
  | "zoom_reconnect_required"
  | "zoom_unavailable"
  | "unknown";

// Long enough to still be on screen when the provider comes back from the Zoom tab: MUI pauses a
// snackbar's auto-hide while the window is blurred, so the clock only runs once they return.
const FALLBACK_NOTICE_DURATION_MS = 10000;

/**
 * Opens a telehealth visit from the appointment's video icon.
 *
 * Every Zoom meeting Suno creates is owned by the clinic's single connected Zoom account, and
 * the only link we have ever surfaced is the guest `join_url` — the same one the patient gets.
 * A provider who is not signed in as that shared account is therefore blocked by Zoom with
 * "host required sign-in to start the appointment" (SN-19531). With the flag on, the icon
 * instead mints a fresh *host* start link so whoever clicks it starts the visit.
 *
 * Every caller of this is an internal staff screen — patients receive their link through the
 * reminder messages, not this button — so there is no guest/host choice to make here and
 * deliberately no second control: under time pressure with a patient waiting, a wrong button
 * fails exactly the way the bug we are fixing does.
 */
export function useZoomJoin(appointmentId: number | undefined) {
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const isHostStartLinkEnabled = Boolean(flags[ZOOM_HOST_START_LINK_FEATURE]);

  // `reset` drops the mutation result from the redux store as soon as we are done with it.
  // RTK would otherwise hold the returned URL until this component unmounts, and that URL is a
  // credential — anyone holding it is the host of the visit — so it has no business sitting in
  // application state (or Redux DevTools) any longer than the moment it takes to open the tab.
  const [getZoomStartLink, { reset: resetStartLinkResult }] =
    useGetZoomStartLinkMutation();
  const [isJoining, setIsJoining] = useState(false);
  // A ref, not `isJoining`: the callback closes over the state value from the render it was
  // created in, so a state read here would still be false on a fast second click. Each click
  // mints a new host token, and a second one may displace whoever is already hosting.
  const isJoiningRef = useRef(false);

  const handleJoinZoom = useCallback(
    async (joinUrl: string) => {
      if (!isHostStartLinkEnabled || !appointmentId) {
        // Flag off: byte-identical to the behaviour before this feature — a synchronous open
        // of the guest link, no request issued.
        window.open(joinUrl, "_blank");
        return;
      }

      if (isJoiningRef.current) return;

      // Claim the tab NOW, while the click's transient user activation is still live. Opening
      // after the await instead would be blocked by Safari every time and Chrome usually, and
      // the provider would be told to allow pop-ups they had already allowed.
      const tab = window.open("", "_blank");

      isJoiningRef.current = true;
      setIsJoining(true);
      const fallBackToGuestLink = (error?: unknown) => {
        // Falling back to the guest link makes the worst case equal to the flag-off behaviour;
        // a provider with a patient waiting must never be left holding a dead button. But the
        // fallback must not be SILENT: Zoom then shows "host required sign-in", which looks
        // exactly like the bug this feature fixed, and a 100% failure rate went unnoticed for
        // weeks that way (ENG-2085).
        openInTab(tab, joinUrl);
        const { code, hostEmail } = readFailure(error);
        // Clear what a previous toast may have left behind, as the global error toast does.
        dispatch(setLabel(""));
        dispatch(setAdditionalAction(undefined));
        dispatch(setCustomMessage(hostStartFailureMessage(code, hostEmail)));
        dispatch(setOpen(FALLBACK_NOTICE_DURATION_MS));
        datadogRum.addAction("zoom_host_start_fallback", {
          appointment_id: appointmentId,
          code
        });
      };

      try {
        const { url } = await getZoomStartLink(appointmentId).unwrap();
        if (url) {
          openInTab(tab, url);
        } else {
          fallBackToGuestLink();
        }
      } catch (error) {
        // A coded 409/502 from the endpoint, or a timeout, a network error or a backend that
        // predates the endpoint -- all of which read as "unknown".
        fallBackToGuestLink(error);
      } finally {
        resetStartLinkResult();
        isJoiningRef.current = false;
        setIsJoining(false);
      }
    },
    [
      appointmentId,
      dispatch,
      getZoomStartLink,
      isHostStartLinkEnabled,
      resetStartLinkResult
    ]
  );

  return { handleJoinZoom, isJoining };
}

function openInTab(tab: Window | null, url: string) {
  if (tab && !tab.closed) {
    // `replace`, so the blank placeholder is not left in the new tab's history.
    tab.location.replace(url);
    return;
  }
  // The tab was blocked outright, or the user closed it while we were waiting. One more
  // attempt is the best we can do; a hard pop-up block would have stopped the old direct
  // `window.open` too, so this is not a regression either way.
  window.open(url, "_blank");
}

function readFailure(error: unknown): {
  code: HostStartFailureCode;
  hostEmail?: string;
} {
  const data = (error as { data?: { code?: unknown; host_email?: unknown } })
    ?.data;
  const code =
    data?.code === "zoom_reconnect_required" ||
    data?.code === "zoom_unavailable"
      ? data.code
      : "unknown";
  const hostEmail =
    typeof data?.host_email === "string" && data.host_email
      ? data.host_email
      : undefined;
  return { code, hostEmail };
}

function hostStartFailureMessage(
  code: HostStartFailureCode,
  hostEmail?: string
): string {
  if (code === "zoom_reconnect_required") {
    const account = hostEmail ?? "the clinic's connected Zoom account";
    return `Couldn't start as host. Sign in to Zoom as ${account} or ask an admin to reconnect Zoom.`;
  }
  if (code === "zoom_unavailable") {
    return "Couldn't start as host because Zoom didn't respond. Opened the guest link instead.";
  }
  return "Couldn't start as host. Opened the guest link instead.";
}
